import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Button } from 'primereact/button';
import { Controller, useForm } from 'react-hook-form';

import { loadIcon, normalizeGsm7 } from 'utils/utils';
import constants from 'constants';
import InfiniteScrollList from './InfiniteScrollList';
import TextSnippetWrapper from 'components/TextSnippetWrapper';

const TEXT_MESSAGE_CHAR_LIMIT = 1600;

const getFileType = (fileData) => {
  try {
    let file_type = fileData?.type?.toString();
    file_type = constants.upload_doc_types[file_type];
    return file_type;
  } catch (error) {
    return false;
  }
};

export default function MessageBox(props) {
  const {
    isNewThread,
    createNewThread,
    uploadindStatus,
    listRef,
    currentOffset,
    hasMoreMessages,
    caseLeadList,
    messages,
    loading,
    fetchMessageHistory,
    file,
    removeFile,
    sendMessage,
    selectedThread,
    newThreadActive,
    navigateToLatest,
    fileOnChange,
    isClientOptIn,
    selectedContact,
  } = props;

  const hiddenFileInput = React.useRef(null);
  const scrollContainerRef = useRef(null);
  const {
    control,
    handleSubmit,
    formState: { isValid, errors },
    reset,
    trigger,
    watch,
  } = useForm({ shouldValidate: true });
  const [loadSentMsg, setLoadSentMsg] = useState(false);

  const messageValue = watch('message') || '';
  const messageLength = messageValue?.length || 0;
  const isOverLimit = messageLength > TEXT_MESSAGE_CHAR_LIMIT;

  const handleFileUploadClick = () => hiddenFileInput?.current?.click();

  const submit = async (data) => {
    const message = normalizeGsm7(data?.message || '');
    setLoadSentMsg(true);
    sendMessage({ ...data, message }).then(() => {
      setLoadSentMsg(false);
      reset();
      handleClear();
    });
  };

  const validate = (message) => Boolean(file || message?.trim());

  const handleDrop = (e) => {
    e.preventDefault();
    fileOnChange(e);
  };

  // Trigger validation on filechange
  useEffect(() => {
    trigger();
  }, [file]);

  const latestPhoneNum = useMemo(() => {
    // Try to get phone number from multiple sources with fallbacks
    let phoneNumber = '';
    let countryCode = selectedThread?.country_code || selectedContact?.country_code || '+1';

    // First, try latest_phone_number with country_code
    if (selectedThread?.latest_phone_number && selectedThread?.country_code) {
      phoneNumber = selectedThread?.latest_phone_number.startsWith(selectedThread?.country_code)
        ? selectedThread?.latest_phone_number
        : `${selectedThread?.country_code}${selectedThread?.latest_phone_number}`;
    }

    // Fallback to selectedThread?.to if latest_phone_number is not available
    if (!phoneNumber && selectedThread?.to) {
      phoneNumber = selectedThread?.to;
    }

    // Fallback to selectedThread?.phone if to is not available
    if (!phoneNumber && selectedThread?.phone) {
      phoneNumber = selectedThread?.phone;
    }

    // Final fallback to selectedContact?.phone
    if (!phoneNumber && selectedContact?.phone) {
      phoneNumber = selectedContact?.phone;
    }

    // Ensure country code is present in the phone number
    if (phoneNumber && !phoneNumber.startsWith('+') && !phoneNumber.startsWith(countryCode)) {
      phoneNumber = `${countryCode}${phoneNumber}`;
    }

    return phoneNumber;
  }, [selectedThread, selectedContact]);

  const handleClear = () => {
    if (hiddenFileInput.current) {
      hiddenFileInput.current.value = '';
    }
    removeFile();
  };

  // Helper function to detect if phone number has changed
  // Compares thread's phone (to) with contact's latest phone number
  // This helps detect phone changes for Business/Location contacts where backend may not set is_active: false
  const hasPhoneChanged = () => {
    if (!selectedThread?.to || !selectedThread?.latest_phone_number) {
      return false;
    }

    // Normalize phone numbers for comparison (remove formatting and country codes)
    const normalizePhone = (phone) => {
      let normalized = phone.replace(/[^\d]/g, ''); // Remove all non-digits (+, spaces, dashes, etc.)
      // Remove leading 1 for US numbers (country code)
      if (normalized.startsWith('1') && normalized.length === 11) {
        normalized = normalized.substring(1);
      }
      return normalized;
    };

    const normalizedTo = normalizePhone(selectedThread.to);
    const normalizedLatest = normalizePhone(selectedThread.latest_phone_number);

    // Return true if phone numbers are different
    return normalizedTo !== normalizedLatest;
  };

  return (
    <div className="col-md-8 col-12 pe-md-2 d-flex flex-column overflow-hidden">
      <div className="flex-grow-1 overflow-auto" ref={scrollContainerRef}>
        <InfiniteScrollList
          {...{ currentOffset, hasMoreMessages, listRef, selectedThread, messages, loading, fetchMessageHistory, scrollContainerRef }}
        />
      </div>
      <div>
        {isClientOptIn === false && (
          <div className="flex-column mb-3 d-flex justify-content-center align-items-center">
            <div className="mt-4 F-size14 pb-2">You can't send a message. The client has not opted to receive a Text Message.</div>
          </div>
        )}
        {selectedThread && (
          <div className="flex-column mb-3 d-flex justify-content-center align-items-center">
            {selectedThread?.contact_status === 'ARCHIVED' && (
              <div className="mt-4 F-size14 pb-2">You can't send message. Contact is Inactive!</div>
            )}
            {selectedThread?.removed_phone_number === true && (
              <div className="mt-4 F-size14 pb-2">You can't send message. Contact phone number is removed</div>
            )}
            {hasPhoneChanged() && Boolean(selectedThread?.removed_phone_number) === false && (
              <div className="mt-3 F-size14 pb-2">Changed their phone number to a new number ({latestPhoneNum})</div>
            )}
            {caseLeadList?.length === 0 && (
              <div className="mt-5 F-size14 pb-2 text-center">
                Message cannot be sent to this thread, since this contact is not been assigned to any case or Lead.
              </div>
            )}

            {/* Show "Go To Thread" button when phone number has changed */}
            {hasPhoneChanged() && !isNewThread && Boolean(selectedThread?.removed_phone_number) === false && caseLeadList?.length > 0 ? (
              <Button
                className="p-button-secondary"
                type="button"
                label="Go To Thread"
                onClick={() => navigateToLatest(selectedThread.contact_id)}
              />
            ) : /* Show "Create A New Thread" button for new chats with no messages */
            messages.length === 0 &&
              !loading &&
              !isNewThread &&
              !selectedThread?.fromGoToThread &&
              !selectedThread?.fromCreateThread &&
              Boolean(selectedThread?.removed_phone_number) === false &&
              caseLeadList?.length > 0 ? (
              <Button
                className="p-button-secondary"
                type="button"
                label="Create A New Thread"
                onClick={() => createNewThread(latestPhoneNum)}
              />
            ) : null}
          </div>
        )}
        {/* Show send form for existing chats, or after clicking "Create A New Thread" button */}
        {/* Hide send form if phone number has changed (prevents sending to old number) */}
        {!hasPhoneChanged() &&
          caseLeadList?.length > 0 &&
          selectedThread?.contact_status !== 'ARCHIVED' &&
          Boolean(selectedThread?.removed_phone_number) === false &&
          isClientOptIn !== false &&
          (newThreadActive || isNewThread || selectedThread?.fromGoToThread || selectedThread?.fromCreateThread) &&
          (messages.length > 0 || isNewThread || loading || selectedThread?.fromGoToThread || selectedThread?.fromCreateThread) && (
            <div className="col-sm-12">
              <form onSubmit={handleSubmit(submit)}>
                <div className="text-message-input-wrapper">
                  <div onDrop={handleDrop} style={{ position: 'relative' }}>
                    <Controller
                      name="message"
                      rules={{
                        validate,
                        maxLength: {
                          value: TEXT_MESSAGE_CHAR_LIMIT,
                          message: `Message cannot exceed ${TEXT_MESSAGE_CHAR_LIMIT} characters`,
                        },
                      }}
                      render={({ field }) => (
                        <TextSnippetWrapper
                          type="textarea"
                          rows={4}
                          cols={30}
                          autoResize
                          className={`border shadow-small w-100 text-message-input ${isOverLimit ? 'p-invalid' : ''}`}
                          placeholder="Send a Message..."
                          {...field}
                        />
                      )}
                      control={control}
                      defaultValue={''}
                    />

                    <div className="d-flex align-items-center justify-content-between mt-2">
                      <div className="flex-grow-1">
                        {isOverLimit && (
                          <small className="d-block text-danger">Message cannot exceed {TEXT_MESSAGE_CHAR_LIMIT} characters</small>
                        )}
                        {errors?.message && !isOverLimit && <small className="d-block text-danger">{errors.message.message}</small>}
                      </div>
                      <small className={`ms-2 ${isOverLimit ? 'text-danger fw-bold' : 'text-muted'}`} style={{ whiteSpace: 'nowrap' }}>
                        {messageLength}/{TEXT_MESSAGE_CHAR_LIMIT}
                      </small>
                    </div>

                    <div className="d-flex align-items-center justify-content-end mt-2">
                      <i className="pi pi-upload pointer me-3" onClick={handleFileUploadClick} />
                      <input type="file" onChange={fileOnChange} style={{ display: 'none' }} ref={hiddenFileInput} />
                      <Button
                        className="p-button-secondary"
                        type="button"
                        disabled={!Boolean(isValid) || hasPhoneChanged() || uploadindStatus || loadSentMsg || isOverLimit}
                        label="Send"
                        loading={uploadindStatus}
                        onClick={handleSubmit(submit)}
                      />
                    </div>
                  </div>
                </div>

                <div className="my-2">
                  {file && (
                    <div className="d-flex align-items-center ms-2">
                      <i className="fas fa-paperclip me-2"></i>
                      {getFileType(file) && loadIcon(getFileType(file))}
                      <span className="d-flex">
                        <p className="text-decoration-underline mb-0 text-muted">{file.name}</p> {uploadindStatus && '- Uploading...'}
                      </span>
                      {!Boolean(uploadindStatus) && (
                        <div className="d-flex ms-2">
                          <i onClick={handleClear} className="icon-delete ms-2 pointer icon-red" />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </form>
            </div>
          )}
      </div>
    </div>
  );
}
